import { Dungeon } from '../dungeon/Dungeon';
import { Vec3 } from '../types';
import { Geometry } from '../renderer/geometry';
import { random, shuffle } from '../utils/random';

export interface SpeedBoost {
  position: Vec3;
  rotation: number;
  collected: boolean;
}

export const SPEED_BOOST_COLLECT_RADIUS = 0.35;
export const SPEED_BOOST_DURATION = 5000; // 5 seconds in milliseconds
export const SPEED_BOOST_MULTIPLIER = 1.8; // 80% faster

export function placeSpeedBoost(
  dungeon: Dungeon,
  excludeCell?: { x: number; z: number },
  excludeRadius = 5
): SpeedBoost | null {
  const candidates: { x: number; z: number }[] = [];

  // Find cells that are far enough from the excluded cell
  for (let z = 1; z < dungeon.height - 1; z++) {
    for (let x = 1; x < dungeon.width - 1; x++) {
      if (!dungeon.isWalkable(x, z)) continue;

      if (excludeCell) {
        const dx = x - excludeCell.x;
        const dz = z - excludeCell.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist < excludeRadius) continue;
      }

      candidates.push({ x, z });
    }
  }

  if (candidates.length === 0) return null;

  shuffle(candidates);
  const cell = candidates[0];
  const worldPos = dungeon.cellToWorld(cell.x, cell.z);

  return {
    position: {
      x: worldPos.x,
      y: 0.5,
      z: worldPos.z
    },
    rotation: random() * Math.PI * 2,
    collected: false
  };
}

// Generate icosahedron geometry (same as star but slightly larger)
export function generateSpeedBoostGeometry(): Geometry {
  const vertices: number[] = [];
  const scale = 0.10; // Slightly larger than star (0.08)

  // Golden ratio for icosahedron construction
  const phi = (1 + Math.sqrt(5)) / 2;

  // 12 vertices of icosahedron
  const icosaVerts: number[][] = [
    [-1,  phi,  0], [ 1,  phi,  0], [-1, -phi,  0], [ 1, -phi,  0],
    [ 0, -1,  phi], [ 0,  1,  phi], [ 0, -1, -phi], [ 0,  1, -phi],
    [ phi,  0, -1], [ phi,  0,  1], [-phi,  0, -1], [-phi,  0,  1]
  ];

  // Scale vertices
  for (const v of icosaVerts) {
    v[0] *= scale;
    v[1] *= scale;
    v[2] *= scale;
  }

  // 20 triangular faces of icosahedron (CCW winding)
  const icosaFaces: number[][] = [
    [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
    [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
    [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
    [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1]
  ];

  // Helper to calculate face normal
  function calcNormal(p0: number[], p1: number[], p2: number[]): number[] {
    const v1 = [p1[0] - p0[0], p1[1] - p0[1], p1[2] - p0[2]];
    const v2 = [p2[0] - p0[0], p2[1] - p0[1], p2[2] - p0[2]];

    const n = [
      v1[1] * v2[2] - v1[2] * v2[1],
      v1[2] * v2[0] - v1[0] * v2[2],
      v1[0] * v2[1] - v1[1] * v2[0]
    ];

    const len = Math.sqrt(n[0] ** 2 + n[1] ** 2 + n[2] ** 2);
    return [n[0] / len, n[1] / len, n[2] / len];
  }

  // Build triangles (9 floats per vertex: position(3) + normal(3) + uv(2) + texIndex(1))
  for (const face of icosaFaces) {
    const p0 = icosaVerts[face[0]];
    const p1 = icosaVerts[face[1]];
    const p2 = icosaVerts[face[2]];
    const normal = calcNormal(p0, p1, p2);

    vertices.push(...p0, ...normal, 0, 0, 0);
    vertices.push(...p1, ...normal, 0, 0, 0);
    vertices.push(...p2, ...normal, 0, 0, 0);
  }

  return {
    vertices: new Float32Array(vertices),
    vertexCount: vertices.length / 9
  };
}
